import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@tmr/api-client';

/**
 * Server-state cache for the portal (09 §5): data that lives in the backend is cached and invalidated
 * by key, never mirrored into component state. Client state — filters, active tab, form drafts — stays
 * in React and never comes near this cache.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // A request the server refused will be refused again: retrying a 4xx only delays the error the
        // operator needs to see. Server and network faults still get one retry.
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status < 500) return false;
          return failureCount < 1;
        },
        // Staff move between list and detail constantly; a short window avoids refetching on every hop
        // without ever showing data old enough to mislead.
        staleTime: 30_000,
        // Off by default, and opted into per query (`09` §11, N2). On globally it would re-read reference
        // data, catalogues and lists that only this portal can change, on every alt-tab. The queries that
        // turn it on are the ones whose subject moves somewhere else — a contract signed at the provider,
        // an export finishing in a worker — where returning to the tab is the signal that something may
        // have happened. `staleTime` still throttles a quick hop away and back.
        refetchOnWindowFocus: false,
      },
      mutations: {
        // Writes are never retried automatically — a retried POST can duplicate work the operator
        // did not ask for twice.
        retry: false,
      },
    },
  });
}

/**
 * Query keys, built from the most specific segment outwards so a whole resource can be invalidated at
 * once: after a write to company X, `invalidateQueries({ queryKey: companyKeys.detail(X) })` also
 * refreshes its activity and rates.
 */
export const companyKeys = {
  all: ['company'] as const,
  list: (filters: Record<string, unknown> = {}) => ['company', 'list', filters] as const,
  detail: (companyId: string) => ['company', companyId] as const,
  onboardingStatus: (companyId: string) => ['company', companyId, 'onboarding-status'] as const,
  /**
   * The manager pickers' candidates, shared by both roles. Filed under Empresas rather than the operator
   * directory because that is whose endpoint it is (and whose permissions gate it) — the picker reads
   * `GET /companies/managers`, never `GET /users`.
   *
   * One cache entry per term, not per role: the two fields search the same directory, so typing the same
   * name into the second picker should hit what the first already fetched.
   */
  managers: (query: Record<string, unknown> = {}) => ['company', 'managers', query] as const,
  /**
   * The Empresa's receiving accounts, owned by the Finance module (13). Nested under the Empresa's key
   * even though it is another module's resource: the screen that reads it is the Empresa's, and after
   * an edit both have to refresh together.
   */
  receivingAccounts: (companyId: string) => ['company', companyId, 'receiving-accounts'] as const,
  /**
   * The Empresa's conta corrente — its internal ledger account, not a bank account (14 §1). Nested under
   * the Empresa for the same reason as `receivingAccounts`, and named apart from it deliberately: the two
   * are different concepts that the legacy keeps confusing, and one key holding both would repeat that.
   */
  currentAccount: (companyId: string) => ['company', companyId, 'current-account'] as const,
  /**
   * The classification catalogue (12 §3). Not under any Empresa — it is reference data shared by every
   * registration screen, so one entry serves the wizard, the editor and the detail at once.
   */
  classifications: ['company', 'classifications'] as const,
  /**
   * The Empresa's rates (08). Nested under the Empresa like `receivingAccounts`, and for the same reason:
   * an Empresa's rate is only ever read in the context of the Empresa it belongs to, and there is no
   * portfolio-wide view of *those* for a root key to serve — the cross-company one is F16.
   *
   * Not to be read as "no rate query lives outside an Empresa": the global template does, under
   * `globalRateKeys`. It is a different resource, not a wider view of this one.
   */
  rates: (companyId: string, filters: Record<string, unknown> = {}) =>
    ['company', companyId, 'rates', filters] as const,
  /** Everything under one Empresa's rates, whatever the filters — what a write invalidates. */
  ratesAll: (companyId: string) => ['company', companyId, 'rates'] as const,
  /**
   * The rate-type and credit-engine vocabularies (08 §5). Not under any Empresa: like the classification
   * catalogue it is reference data read from the legacy, shared by every rate form.
   */
  rateCatalog: ['company', 'rate-catalog'] as const,

  /** One Empresa's rates compared against the legacy. Never cached across visits — it is a live check. */
  rateDrift: (companyId: string) => ['company', companyId, 'rate-drift'] as const,

  /** One rate's recorded changes (15 R4). Never cached across visits — it is asked when disputed. */
  rateHistory: (companyId: string, rateId: string) =>
    ['company', companyId, 'rate-history', rateId] as const,

  /** One Empresa's legacy products, for the rate form's picker. Read per Empresa, not globally. */
  rateProducts: (companyId: string) => ['company', companyId, 'rate-products'] as const,

  /**
   * One Empresa's timeline, per tab (`07`). The category is part of the key rather than filtered in the
   * component because each tab is a separate cursor walk — sharing one cache entry between them would make
   * "carregar mais" on Contratos append to whatever Cadastro had already loaded.
   */
  activity: (companyId: string, category: string) =>
    ['company', companyId, 'activity', category] as const,
};

/**
 * Keys for the TMR's global rate template (15 §3 A) — the first rate query in the portal that is not scoped
 * to an Empresa.
 *
 * Its own root rather than a branch of `companyKeys`: `taxas_tmr` has no `produtor_id`, so there is no
 * Empresa to nest it under. And a template write invalidates only itself — the Empresas a propagation
 * reaches are rewritten in the legacy by a worker, on its own schedule, so there is no moment at which
 * invalidating their caches here would show the new value.
 */
export const globalRateKeys = {
  all: ['global-rate'] as const,
  list: () => ['global-rate', 'list'] as const,
  /** One line's blast radius. Never served from cache — it is counted in the legacy at the moment asked. */
  impact: (rateId: string) => ['global-rate', rateId, 'impact'] as const,
};

/**
 * Keys for the business rules (`25`) — the TMR's defaults and each Empresa's own values.
 *
 * One root for both, and not a branch of `companyKeys`, because a write on either side moves the other: a
 * new default changes what every Empresa without an override sells with, and an override changes the count
 * the global screen shows. Every write invalidates the root, which is a handful of small reads.
 */
export const businessRuleKeys = {
  all: ['business-rule'] as const,
  list: () => ['business-rule', 'list'] as const,
  company: (companyId: string) => ['business-rule', 'company', companyId] as const,
  overrides: (ruleKey: string) => ['business-rule', 'overrides', ruleKey] as const,
};

/**
 * Keys for the credit rating's screens (26): the queue and each evaluation. The customer lookup is not cached — its
 * CPF travels in a POST body, never in a key.
 */
/**
 * Keys for the Contingência panel (`30`): the kinds with their setting, and the requests. A write invalidates the
 * root — a setting moves the cards, a request moves the list and the pending count on the card.
 */
export const contingencyKeys = {
  all: ['contingency'] as const,
  types: () => ['contingency', 'types'] as const,
  requests: (filters: Record<string, unknown> = {}) => ['contingency', 'requests', filters] as const,
};

/**
 * Keys for the clinic applications from the public site (28). Their own root: an application is not an
 * Empresa yet. Approving one also creates an Empresa, so that write invalidates `companyKeys.all` too.
 */
export const companyApplicationKeys = {
  all: ['company-applications'] as const,
  list: (filters: Record<string, unknown> = {}) => ['company-applications', 'list', filters] as const,
  detail: (applicationId: string) => ['company-applications', applicationId] as const,
};

export const creditKeys = {
  all: ['credit'] as const,
  reviews: () => ['credit', 'manual-reviews'] as const,
  review: (evaluationId: string) => ['credit', 'manual-reviews', evaluationId] as const,
};

/**
 * Keys for the rate portfolio (15 R3) — the Empresas whose rates disagree with the legacy.
 *
 * Its own root rather than a branch of `companyKeys`, even though the subject is Empresa rates: this is the
 * cross-company view, and a write here can move any Empresa. Nesting it under one would make invalidation
 * either too narrow or a blunt instrument on the whole Empresa root.
 *
 * Keyed by cursor because the endpoint pages by one. Nothing is served from cache (see `useRatesOutOfSync`)
 * — the key exists for the devtools and for explicit invalidation after a reconciliation.
 */
export const ratePortfolioKeys = {
  all: ['rate-portfolio'] as const,
  outOfSync: (after: string) => ['rate-portfolio', 'out-of-sync', after] as const,
};

/**
 * Keys for contracts, owned by the Contracts module (02/03).
 *
 * Its own root rather than nested under the Empresa, unlike `receivingAccounts`: a contract is read both per
 * Empresa (the detail tab) and across all of them (the Contratos screen), so the portfolio view has to be
 * invalidatable without knowing which Empresa changed.
 */
export const contractKeys = {
  all: ['contract'] as const,
  list: (filters: Record<string, unknown> = {}) => ['contract', 'list', filters] as const,
  detail: (contractId: string) => ['contract', contractId] as const,
  /**
   * The provider's live view of the document. Keyed like any other query for the sake of the devtools and
   * of an explicit refetch — but it is never served from cache: the download links it carries expire within
   * minutes (see `useContractDocument`).
   */
  document: (contractId: string) => ['contract', contractId, 'document'] as const,
  /**
   * What sending the contract would produce. Also never served from cache, for the opposite reason to the
   * document's: it describes the Empresa's record at this instant, and an operator who fixed the cadastre
   * has to see the correction rather than the version they just decided not to send.
   */
  preview: (contractId: string) => ['contract', contractId, 'preview'] as const,
  /**
   * The representative's signing link, read from the provider. Never served from cache: replacing the
   * signatory invalidates it (see `useSigningLink`).
   */
  signingLink: (contractId: string) => ['contract', contractId, 'signing-link'] as const,
};

/**
 * Keys for the patient contract templates (`24` §9).
 *
 * Deliberately not nested under `contractKeys`: that one is the Empresa's accreditation contract, and the
 * two are governed by different permissions. Sharing a key space would make one screen's invalidation
 * refetch the other's data for no reason.
 */
export const contractTemplateKeys = {
  all: ['contract-template'] as const,
  list: () => ['contract-template', 'list'] as const,
  detail: (templateId: string) => ['contract-template', templateId] as const,
  /** The document type catalogue — code on the backend, so it is the same for everybody. */
  types: () => ['contract-template', 'types'] as const,
  diff: (templateId: string, versionId: string, against: string) =>
    ['contract-template', templateId, 'diff', versionId, against] as const,
  /**
   * A published version rendered with the type's example values. Keyed by version because a published
   * version never changes — the one thing on this screen that can be cached for good.
   */
  rendered: (templateId: string, versionId: string) =>
    ['contract-template', templateId, 'rendered', versionId] as const,
  /** What the v1.1 holds that is not here yet — the initial load's preview (`24` P2b). */
  legacyImport: () => ['contract-template', 'legacy-import'] as const,
};

/**
 * Keys for the contracts patients signed (`24` §3.3).
 *
 * Separate from both `contractKeys` (the Empresa's accreditation) and `contractTemplateKeys` (the text
 * these are issued from): three subjects, three permissions, and an invalidation on one should not refetch
 * the others.
 */
export const patientContractKeys = {
  all: ['patient-contract'] as const,
  lists: () => ['patient-contract', 'list'] as const,
  list: (filters: Record<string, unknown> = {}) => ['patient-contract', 'list', filters] as const,
  detail: (documentId: string) => ['patient-contract', documentId] as const,
  /** The frozen text, fetched only when the reading view opens — it is a whole contract. */
  document: (documentId: string) => ['patient-contract', documentId, 'document'] as const,
};

/**
 * Keys for the Finance module's own reference data. Not nested under an Empresa: the bank catalogue is
 * shared by every account form, so one entry serves them all.
 */
export const financeKeys = {
  banks: ['finance', 'banks'] as const,
};

/** Keys for the operator directory (`GET /users`), read by the Gestor da Conta picker (10 B5). */
export const userKeys = {
  all: ['user'] as const,
  search: (query: Record<string, unknown> = {}) => ['user', 'search', query] as const,
  list: (query: Record<string, unknown> = {}) => ['user', 'list', query] as const,
  /**
   * The legacy login picker (`GET /users/legacy`, 05 I7). Under the same root on purpose: each row says
   * whether another account already holds it, so linking somebody has to invalidate this too.
   */
  legacySearch: (term: string) => ['user', 'legacy-search', term] as const,
  /** The ScriptCase staff profiles (`GET /users/legacy-profiles`, 33) — reference data read from the legacy. */
  legacyProfiles: () => ['user', 'legacy-profiles'] as const,
  /** The screens one ScriptCase profile opens (`GET /users/legacy-profiles/{id}/screens`). */
  legacyProfileScreens: (profileId: number) => ['user', 'legacy-profiles', profileId, 'screens'] as const,
};

/**
 * Keys for the Parties directory (`GET /parties`) — the Partes screen and the Empresa cadastre's people
 * pickers (10 B5b).
 *
 * One root for both because they are the same directory: creating somebody from a cadastre block has to
 * show up on the Partes list, and correcting them there has to reach the picker that suggested them.
 */
export const partyKeys = {
  all: ['party'] as const,
  list: (filters: Record<string, unknown> = {}) => ['party', 'list', filters] as const,
  search: (query: Record<string, unknown> = {}) => ['party', 'search', query] as const,
  detail: (partyId: string) => ['party', partyId] as const,
};

/** Keys for the Holdings directory (`GET /holdings`), read by the grant scope picker (10 B6b). */
export const holdingKeys = {
  all: ['holding'] as const,
  search: (query: Record<string, unknown> = {}) => ['holding', 'search', query] as const,
};

/**
 * Keys for the authorization tooling (10 B7). Roles, the approval queue and the audit log share the
 * `authorization` root because a write to any of them shows up in the others: creating a role writes an
 * audit entry, approving a request grants roles. Invalidating the root after a write keeps the three
 * screens honest with each other.
 */
export const authorizationKeys = {
  all: ['authorization'] as const,
  features: () => ['authorization', 'features'] as const,
  roles: () => ['authorization', 'roles'] as const,
  role: (roleId: string) => ['authorization', 'roles', roleId] as const,
  auditLog: (filters: Record<string, unknown> = {}) => ['authorization', 'audit-log', filters] as const,
  accessRequests: (filters: Record<string, unknown> = {}) =>
    ['authorization', 'access-requests', filters] as const,
};
